import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { WizardLayout } from "@/components/we360/WizardLayout";
import DemoFormOverlay from "@/components/DemoFormOverlay";
import { useFormValidation } from "@/hooks/useFormValidation";
import { CheckCircle } from "lucide-react";
import { syncF1 } from "@/services/backendSync";
import { saveMeasurement, syncMetricsToGCP } from "@/services/measurements";
import { validateWhatsAppPhone, splitWhatsAppPhone } from "@/services/coreServicesClient";
import Step1Welcome from "./Step1Welcome";
import Step2Personal from "./Step2Personal";
import Step3Clinical from "./Step3Clinical";
import Step4History from "./Step4History";
import Step5Lifestyle from "./Step5Lifestyle";
import Step6Meds from "./Step6Meds";
import Step7Consent from "./Step7Consent";
import {
  calculateIMC,
  classifyIMC,
  classifyBP,
  classifyGlucose,
  getAutoDetectedConditions,
} from "./helpers";

const STEPS = [
  { id: "welcome", title: "Bienvenido/a", sub: "Comencemos tu perfil de salud" },
  { id: "demo", title: "Datos Personales", sub: "Cuéntanos sobre ti" },
  { id: "clinical", title: "Parámetros Clínicos", sub: "Tus medidas de salud" },
  { id: "history", title: "Antecedentes", sub: "Tu historial médico" },
  { id: "lifestyle", title: "Estilos de Vida", sub: "Tus hábitos diarios" },
  { id: "meds", title: "Medicamentos", sub: "Tratamientos actuales" },
  { id: "consent", title: "Consentimiento", sub: "Último paso" },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_REGEX = /^[a-zA-ZáéíóúñÁÉÍÓÚÑüÜ\s'-]+$/;

function getStepRules(step, data) {
  switch (step) {
    case 0: return {
      email: { required: true, pattern: EMAIL_REGEX, message: "Ingresa un correo electrónico válido" },
      whatsapp: {
        required: false,
        custom: (v) => {
          if (!v || v.trim() === "") return null;
          const cc = data?.countryCode || splitWhatsAppPhone(v, "+507").countryCode;
          const check = validateWhatsAppPhone(v, cc);
          if (!check.valid) return check.error || "Ingresa un número de teléfono válido";
          return null;
        },
      },
    };
    case 1: return {
      nombres: { required: true, minLength: 2, pattern: NAME_REGEX, message: "Ingresa tu nombre" },
      apellidos: { required: true, minLength: 2, pattern: NAME_REGEX, message: "Ingresa tu apellido" },
      fechaNacimiento: {
        required: true,
        custom: (v) => {
          if (!v) return "Ingresa una fecha de nacimiento válida";
          const d = new Date(v);
          if (isNaN(d.getTime())) return "Ingresa una fecha de nacimiento válida";
          const now = new Date();
          if (d > now) return "Ingresa una fecha de nacimiento válida";
          const age = Math.floor((now - d) / (365.25 * 24 * 60 * 60 * 1000));
          if (age < 12) return "Debes tener al menos 12 años";
          if (age > 120) return "Ingresa una fecha de nacimiento válida";
          return null;
        },
      },
      sexo: { required: true, message: "Selecciona tu sexo" },
    };
    case 2: return {
      peso: { required: false, min: 20, max: 300, message: "Valor fuera de rango (20-300 kg)" },
      talla: { required: false, min: 50, max: 250, message: "Valor fuera de rango (50-250 cm)" },
      cintura: { required: false, min: 40, max: 200, message: "Valor fuera de rango (40-200 cm)" },
      paSistolica: { required: false, min: 60, max: 250, message: "Valor fuera de rango (60-250 mmHg)" },
      paDiastolica: {
        required: false, min: 30, max: 150,
        custom: (v) => {
          if (!v || v === "") return null;
          const num = Number(v);
          if (isNaN(num) || num < 30 || num > 150) return "Valor fuera de rango (30-150 mmHg)";
          const sys = Number(data.paSistolica);
          if (!isNaN(sys) && num >= sys) return "La diastólica debe ser menor que la sistólica";
          return null;
        },
      },
      glucemia: { required: false, min: 30, max: 600, message: "Valor fuera de rango (30-600 mg/dl)" },
      oximetria: { required: false, min: 50, max: 100, message: "Valor fuera de rango (50-100%)" },
    };
    case 3: return {}; // No required validation
    case 4: return {}; // Lifestyle - all optional
    case 5: return {}; // Meds - all optional
    case 6: return {
      consentimiento: {
        required: true,
        custom: (v) => v !== true ? "Debes aceptar el consentimiento para continuar" : null,
      },
    };
    default: return {};
  }
}

export default function FormF1() {
  const { profile, setField, setProfile, toggleCondition, isDemo } = useUserProfile();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [fade, setFade] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();
  const { validateStep, validateField, getError, isStepValid, clearErrors, onFieldChange } = useFormValidation();

  const rules = useMemo(() => getStepRules(step, profile), [step, profile]);

  // Wrap setField to also clear validation errors in real-time
  const handleSetField = useCallback((field, value) => {
    setField(field, value);
    const rule = rules[field];
    if (rule) onFieldChange(field, value, rule);
  }, [setField, rules, onFieldChange]);

  const go = useCallback((dir) => {
    setFade(false);
    setTimeout(() => {
      setStep((s) => s + dir);
      setFade(true);
    }, 200);
  }, []);

  const goNext = useCallback(() => {
    if (isDemo) { go(1); return; }
    const errs = validateStep(rules, profile);
    if (Object.keys(errs).length > 0) return;
    go(1);
  }, [step, rules, profile, validateStep, go, isDemo]);

  const goBack = useCallback(() => {
    clearErrors();
    go(-1);
  }, [go, clearErrors]);

  const handleSubmit = useCallback(async () => {
    if (isDemo) return;
    const errs = validateStep(rules, profile);
    if (Object.keys(errs).length > 0) return;
    if (!profile.consentimiento) return;
    const updatedProfile = {
      ...profile,
      completedAt: new Date().toISOString(),
      fhirSyncStatus: "PENDING_FHIR",
      pendingFhirF1: true,
    };
    setSubmitted(true);
    await setProfile(updatedProfile);

    // Sync mandatorio al backend FHIR (Outbox: syncF1 marcará SYNCED o dejará pendingFhirF1: true para auto-flush)
    const email = user?.email || profile.email;
    if (email) {
      syncF1(updatedProfile, email).then((res) => {
        if (res.ok) {
          console.log("[F1] Backend sync OK:", res.data);
          setProfile((prev) => ({
            ...prev,
            pendingFhirF1: false,
            fhirSyncStatus: "SYNCED",
            fhirSyncedAt: new Date().toISOString(),
          }));
        } else {
          console.warn("[F1] Backend sync pendiente en Outbox:", res);
        }
      });

      // Sincronizar siempre los signos vitales finales de F1 hacia /api/patients/metrics para que prevalezca el último valor ingresado en el formulario
      const manualFields = {};
      ["peso", "talla", "cintura", "paSistolica", "paDiastolica", "glucemia", "oximetria"].forEach((k) => {
        const val = parseFloat(profile[k]);
        if (!isNaN(val) && val > 0) manualFields[k] = val;
      });
      if (Object.keys(manualFields).length > 0) {
        if (profile.vitalsMeasurementMethod !== "camera" && user?.uid) {
          saveMeasurement(user.uid, {
            method: "manual",
            fields: manualFields,
            note: "Signos vitales registrados en Formulario F1",
          })
            .then((savedMeas) =>
              syncMetricsToGCP(email, manualFields, {
                uid: user.uid,
                measurementId: savedMeas?.id || null,
              })
            )
            .catch(() => {});
        } else {
          syncMetricsToGCP(email, manualFields).catch(() => {});
        }
      }
    }

    setTimeout(() => navigate("/dashboard"), 2000);
  }, [profile, rules, validateStep, setProfile, navigate, isDemo, user]);

  // Validate on blur for a specific field
  const handleBlur = useCallback((field) => {
    if (isDemo) return;
    const rule = rules[field];
    if (rule) validateField(field, profile[field], rule);
  }, [rules, profile, validateField, isDemo]);

  const nextDisabled = !isDemo && !isStepValid(rules, profile);

  const imc = calculateIMC(profile.peso, profile.talla);
  const imcClass = classifyIMC(imc);
  const bpClass = classifyBP(profile.paSistolica, profile.paDiastolica);
  const gluClass = classifyGlucose(profile.glucemia);
  const autoDetected = getAutoDetectedConditions(profile, imc);

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 font-sans" style={{ background: "#FCFAEE" }}>
        <div className="we-card p-8 text-center max-w-[380px] w-full" style={{ boxShadow: "0 8px 30px rgba(45,42,36,0.10)" }} data-testid="submit-success-card">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: "#E2F0E8" }}>
            <CheckCircle size={32} color="#157346" />
          </div>
          <h2 className="text-xl font-bold mb-2" style={{ color: "#2D2A24" }}>¡Registro completado!</h2>
          <p className="text-sm" style={{ color: "#71644C" }}>Redirigiendo a tu dashboard...</p>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    const props = { data: profile, setField: handleSetField, errors: { getError }, onBlur: handleBlur };
    switch (step) {
      case 0: return <Step1Welcome {...props} />;
      case 1: return <Step2Personal {...props} />;
      case 2: return <Step3Clinical {...props} imc={imc} imcClass={imcClass} bpClass={bpClass} gluClass={gluClass} />;
      case 3: return <Step4History {...props} autoDetected={autoDetected} toggleCondition={toggleCondition} />;
      case 4: return <Step5Lifestyle {...props} />;
      case 5: return <Step6Meds {...props} />;
      case 6: return <Step7Consent {...props} />;
      default: return null;
    }
  };

  return (
    <DemoFormOverlay>
      <WizardLayout
        steps={STEPS}
        currentStep={step}
        onNext={goNext}
        onBack={goBack}
        fade={fade}
        userName={profile.nombres}
        isLastStep={step === 6}
        onSubmit={handleSubmit}
        submitDisabled={!profile.consentimiento || isDemo}
        nextDisabled={nextDisabled}
      >
        {renderStep()}
      </WizardLayout>
    </DemoFormOverlay>
  );
}
